import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { NumericSpinnerInput } from '@/components/inputs/NumericSpinnerInput'
import styles from './ScopeChangeEditCustomItemsDialog.module.css'

import { IconButton } from '@/components/IconButton'
import cx from 'classnames'

export interface ScopeChangeEditCustomItemsDialogProps {
  showing: boolean
  onClose: () => unknown
  onAddItem: () => unknown
  onSave: () => unknown
  isSavePending: boolean
  /**
   * An `EditScopeChangeCustomItem` per custom item.
   */
  children?: ReactNode
}

export function ScopeChangeEditCustomItemsDialog({
  showing,
  onClose,
  onAddItem,
  onSave,
  isSavePending,
  children,
}: ScopeChangeEditCustomItemsDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSavePending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton>
        <ModalSheetTitle>Edit custom service items</ModalSheetTitle>
      </ModalSheetHeader>
      <CustomItemHeadings />
      <ModalSheetContent>
        <div className={styles.itemsContainer}>
          {children}
          <div className={styles.addCustomItemContainer}>
            <div className={styles.customItemText}>
              Need more custom service items?
            </div>
            <Button
              label="Add new"
              variant="text"
              testId="edit-custom-scope-changes-add-button"
              onPress={onAddItem}
            />
          </div>
        </div>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            isDisabled={isSavePending}
            onPress={onClose}
          />
          <Button
            label="Save"
            onPress={onSave}
            variant="primary"
            testId="scope-change-request-edit"
            isPending={isSavePending}
            isDisabled={isSavePending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

function CustomItemHeadings() {
  return (
    <div className={styles.subheading}>
      <span>Custom service items</span>
      <span>Quantity &nbsp;</span>
    </div>
  )
}

export interface EditScopeChangeCustomItemProps {
  itemKey: string
  name: string
  nameError?: string
  onNameChange: (name: string) => unknown
  quantity: number
  quantityError?: string
  onQuantityChange: (quantity: number | null) => unknown
  onIncrementQuantity: () => unknown
  onDecrementQuantity: () => unknown
  onRemove: () => unknown
}

export function EditScopeChangeCustomItem({
  itemKey,
  name,
  nameError,
  onNameChange,
  quantity,
  quantityError,
  onQuantityChange,
  onIncrementQuantity,
  onDecrementQuantity,
  onRemove,
}: EditScopeChangeCustomItemProps) {
  return (
    <div className={styles.customItemInput}>
      <input
        type="text"
        className={cx(
          styles.input,
          styles.customItemName,
          nameError && styles.hasError,
        )}
        placeholder="Enter your service type"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        data-test={`scope-change-name-${itemKey}`}
      />

      <NumericSpinnerInput
        allowNegative
        value={quantity}
        onChange={onQuantityChange}
        onIncrement={onIncrementQuantity}
        onDecrement={onDecrementQuantity}
        invalid={Boolean(quantityError)}
        testId={`scope-change-quantity-${itemKey}`}
      />

      <IconButton
        name="close"
        label="Remove item"
        className={styles.removeCustomItemButton}
        onPress={onRemove}
        testId={`request-scope-changes-remove-button-${itemKey}`}
        size={24}
      />
    </div>
  )
}
